import { useNavigate, useParams } from "react-router-dom";
import {
  Flex,
  Text,
  IconButton,
  Spinner,
  ScrollArea,
  Container,
} from "@radix-ui/themes";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { EscalationRuleForm } from "@/features/automation-configuration/quackchat-channel/EscalationRuleForm";
import { useSimulatorConfiguration } from "@/features/automation-configuration/useSimulatorConfiguration";
import { useEscalationRuleApi } from "@/v2/entities/escalation-rule/EscalationRule.api";
import { useQuery, useQueryClient } from "react-query";
import { EscalationRuleFormData } from "@/features/automation-configuration/quackchat-channel/EscalationRuleForm";
import {
  EscalationRule,
  EscalationRuleOperationType,
  LiveAgentRoutingStrategy,
  buildCustomFieldsFromFilters,
  buildCustomFieldFiltersFromCustomFields,
} from "@/v2/entities/escalation-rule/EscalationRule.model";
import { EscalationStrategy } from "@/v2/entities/release/Release.model";
import { normalizeStoredCustomFieldFilters } from "@/features/automation-configuration/quackchat-channel/steps/escalationConditionFilters";
import { toast } from "sonner";
import { useCallback, useEffect, useRef, useState } from "react";

export default function EditEscalationRulePage() {
  const navigate = useNavigate();
  const { simulatorId, ruleId } = useParams();
  const { simulator } = useSimulatorConfiguration(simulatorId);
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();
  const initializedRuleIdRef = useRef<string | null>(null);
  const [initialFormValues, setInitialFormValues] =
    useState<EscalationRuleFormData | null>(null);

  const allowLiveAgent = simulator?.channel === "autonomous_quackchat";

  const { getEscalationRule, updateEscalationRule } =
    useEscalationRuleApi(simulatorId);

  const { data: escalationRule, isLoading: isEscalationRuleLoading } = useQuery(
    {
      queryKey: ["escalation-rule", ruleId],
      queryFn: async () => getEscalationRule(ruleId ?? ""),
      enabled: ruleId != null,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (ruleId == null) {
      initializedRuleIdRef.current = null;
      setInitialFormValues(null);
      return;
    }
    if (escalationRule == null || initializedRuleIdRef.current === ruleId)
      return;
    initializedRuleIdRef.current = ruleId;
    setInitialFormValues({
      ...escalationRule,
      customFieldFilters: normalizeStoredCustomFieldFilters(
        buildCustomFieldFiltersFromCustomFields(escalationRule.customFields)
      ),
    });
  }, [ruleId, escalationRule]);

  const handleSubmit = useCallback(
    async (formData: EscalationRuleFormData) => {
      try {
        setIsUpdating(true);
        // Ensure systemMessage is empty string for LIVE_AGENT escalation strategy
        const updatedFormData = {
          ...formData,
          systemMessage:
            formData.escalationStrategy === EscalationStrategy.LIVE_AGENT
              ? ""
              : formData.systemMessage,
          liveAgentRoutingStrategy:
            formData.escalationStrategy === EscalationStrategy.LIVE_AGENT
              ? formData.liveAgentRoutingStrategy ??
                LiveAgentRoutingStrategy.ONLINE_AGENTS
              : undefined,
        };

        if (formData.escalationStrategy === EscalationStrategy.LIVE_AGENT) {
          updatedFormData.escalationRuleOperations =
            formData.escalationRuleOperations.filter(
              (operation) =>
                operation.type === EscalationRuleOperationType.ADD_TAG
            );
        }

        const customFields = buildCustomFieldsFromFilters(
          formData.customFieldFilters
        );
        const { customFieldFilters: _omit, ...formDataForApi } =
          updatedFormData;
        await updateEscalationRule({
          ...formDataForApi,
          simulatorId,
          customFields,
        } as EscalationRule);
        toast.success("Escalation rule updated successfully");
        navigate(`/automation/${simulatorId}/configurations/escalations`);
        queryClient.invalidateQueries(["escalation-rule", ruleId]);
      } catch (error) {
        console.error("Failed to update escalation rule:", error);
        toast.error("Failed to update escalation rule");
      } finally {
        setIsUpdating(false);
      }
    },
    [updateEscalationRule, navigate, simulatorId, queryClient, ruleId]
  );

  const handleCancel = () => {
    navigate(`/automation/${simulatorId}/configurations/escalations`);
  };

  const formReady =
    !isEscalationRuleLoading &&
    (escalationRule == null || initialFormValues != null);

  if (!formReady) {
    return <Spinner />;
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        background: "var(--backgroundDefault)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Flex
        justify="start"
        align="center"
        py={"4"}
        px={"8"}
        gap={"4"}
        style={{
          borderBottom: "1px solid var(--q-border)",
          boxShadow: "var(--shadow-1)",
        }}
      >
        <IconButton variant="ghost" color="gray" onClick={() => navigate(-1)}>
          <ArrowLeftIcon />
        </IconButton>

        <Text size="2" color="gray" weight="medium" highContrast>
          Edit Escalation Rule
        </Text>
      </Flex>

      <ScrollArea style={{ flexGrow: 1 }}>
        <Container size="4" style={{ paddingBlock: 32, maxWidth: "1400px" }}>
          <EscalationRuleForm
            initialValues={initialFormValues ?? undefined}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={isUpdating}
            submitLabel="Update Escalation Rule"
            allowLiveAgent={allowLiveAgent}
          />
        </Container>
      </ScrollArea>
    </div>
  );
}
